<template>
  <form class="space-y-6" @submit.prevent="handleSubmit">
    <!-- Titre -->
    <div class="text-center">
      <h2 class="text-3xl font-bold text-gray-900">
        Mot de passe oublié
      </h2>
      <p class="mt-2 text-sm text-gray-600">
        Entrez votre adresse email pour recevoir un lien de réinitialisation
      </p>
    </div>

    <!-- Alert d'erreur -->
    <Alert 
      v-if="error"
      type="error" 
      :message="error"
      :show="!!error"
      @close="clearError"
    />

    <!-- Alert de succès -->
    <Alert 
      v-if="successMessage"
      type="success" 
      :message="successMessage"
      :show="!!successMessage"
      @close="successMessage = ''"
    />

    <!-- Champ Email -->
    <InputField
      v-model="formData.email"
      type="email"
      name="email"
      label="Adresse email"
      placeholder="votre.email@example.com"
      :required="true"
      :error-message="errors.email"
      :disabled="isSubmitting"
      autocomplete="email"
      :left-icon="MailIcon"
      @blur="validateField('email')"
    />

    <!-- Bouton de soumission -->
    <button
      type="submit"
      :disabled="isSubmitting || !isFormValid"
      :class="submitButtonClasses"
    >
      <LoadingSpinner
        v-if="isSubmitting"
        size="sm"
        variant="white"
        :show-text="false"
      />
      <span v-else>Envoyer le lien de réinitialisation</span>
    </button>

    <!-- Liens de navigation -->
    <div class="text-center space-y-2">
      <p class="text-sm text-gray-600">
        <button
          type="button"
          class="font-medium text-blue-600 hover:text-blue-500 focus:outline-none focus:underline transition-colors"
          @click="$emit('back-to-login')"
        >
          ← Retour à la connexion
        </button>
      </p>
      
      <p class="text-sm text-gray-600">
        Pas encore de compte ?
        <button
          type="button"
          class="font-medium text-blue-600 hover:text-blue-500 focus:outline-none focus:underline transition-colors"
          @click="$emit('switch-to-register')"
        >
          Créer un compte
        </button>
      </p>
    </div>
  </form>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue';
import { Mail as MailIcon } from 'lucide-vue-next';
import InputField from '@/components/form/InputField.vue';
import LoadingSpinner from '@/components/UI/LoadingSpinner.vue';
import Alert from '@/components/UI/Alert.vue';
import { authService } from '@/services/authService';
import type { AuthError } from '@/types/auth';

export interface ForgotPasswordFormEmits {
  (e: 'submit', email: string): void;
  (e: 'back-to-login'): void;
  (e: 'switch-to-register'): void;
}

const emit = defineEmits<ForgotPasswordFormEmits>();

// État du formulaire
const formData = reactive({
  email: '',
});

const errors = reactive({
  email: '',
});

const error = ref<string>('');
const successMessage = ref<string>('');
const isSubmitting = ref(false);

// Validation
const isFormValid = computed((): boolean => {
  return !!(formData.email && !errors.email);
});

const validateField = (field: keyof typeof errors): void => {
  switch (field) {
    case 'email':
      if (!formData.email) {
        errors.email = 'L\'email est requis';
      } else if (!authService.isValidEmail(formData.email)) {
        errors.email = 'Format d\'email invalide';
      } else {
        errors.email = '';
      }
      break;
  }
};

const validateForm = (): boolean => {
  validateField('email');
  return isFormValid.value;
};

const clearError = (): void => {
  error.value = '';
};

const clearErrors = (): void => {
  errors.email = '';
  error.value = '';
  successMessage.value = '';
};

const handleSubmit = async (): Promise<void> => {
  // Nettoyer les erreurs précédentes
  clearErrors();
  
  // Valider le formulaire
  if (!validateForm()) {
    return;
  }
  
  isSubmitting.value = true;
  
  try {
    // Émettre l'événement de soumission
    emit('submit', formData.email.trim());
    
  } catch (err) {
    const authError = err as AuthError;
    error.value = authError.message || 'Une erreur inattendue s\'est produite';
    
    // Erreurs spécifiques aux champs
    if (authError.code === 'USER_NOT_FOUND') {
      errors.email = 'Aucun compte associé à cette adresse email';
    }
    
  } finally {
    isSubmitting.value = false;
  }
};

// Classes computées
const submitButtonClasses = computed((): string => {
  const baseClasses = 'w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2';
  
  if (isSubmitting.value || !isFormValid.value) {
    return `${baseClasses} bg-gray-400 cursor-not-allowed`;
  }
  
  return `${baseClasses} bg-blue-600 hover:bg-blue-700 focus:ring-blue-500`;
});

// Exposer les méthodes pour l'usage externe
defineExpose({
  clearErrors,
  validateForm,
  formData,
  setSuccessMessage: (message: string) => {
    successMessage.value = message;
  },
});
</script> 